import { BRAVEMOVE_PALETTES, PALETTE_KEYS } from '../tokens/palettes';
import type { ColorPalette, PaletteKey } from '../tokens/types';

const COLOR_TO_VAR: Readonly<Record<keyof ColorPalette, string>> = {
  bg: '--bravemove-bg',
  surface: '--bravemove-surface',
  surface2: '--bravemove-surface-2',
  panel: '--bravemove-panel',
  panelInk: '--bravemove-panel-ink',
  ink: '--bravemove-ink',
  ink2: '--bravemove-ink-2',
  ink3: '--bravemove-ink-3',
  muted: '--bravemove-muted',
  hairline: '--bravemove-hairline',
  hairline2: '--bravemove-hairline-2',
  accent: '--bravemove-accent',
  accent2: '--bravemove-accent-2',
  accentInk: '--bravemove-accent-ink',
  accentSoft: '--bravemove-accent-soft',
  primary: '--bravemove-primary',
  primaryHover: '--bravemove-primary-hover',
  primaryInk: '--bravemove-primary-ink',
  accentText: '--bravemove-accent-text',
  secondary: '--bravemove-secondary',
  tertiary: '--bravemove-tertiary',
  success: '--bravemove-success',
  warning: '--bravemove-warning',
  danger: '--bravemove-danger',
  dangerInk: '--bravemove-danger-ink',
  info: '--bravemove-info',
};

export const PALETTE_VAR_MAP = COLOR_TO_VAR;

function paletteToVars(palette: ColorPalette): string {
  return (Object.keys(COLOR_TO_VAR) as (keyof ColorPalette)[])
    .map((key) => `  ${COLOR_TO_VAR[key]}: ${palette[key]};`)
    .join('\n');
}

function selectorFor(key: PaletteKey, isDefault: boolean): string {
  return isDefault ? `:root,\n[data-theme='${key}']` : `[data-theme='${key}']`;
}

/**
 * Build the BraveMove Tailwind v4 theme CSS as a string. The result is byte-for-byte
 * equivalent to packages/design-tokens/src/tailwind/theme.css (the test
 * tailwind-preset.test.ts enforces this).
 *
 * Apps consume the static file via `@import '@bravemove/design-tokens/css/theme.css'`;
 * this function is exported for programmatic consumers (e.g. a future Storybook,
 * codegen tools, or a Vite plugin that inlines the theme).
 */
export function generateThemeCss(): string {
  const blocks = PALETTE_KEYS.map((key, idx) => {
    const palette = BRAVEMOVE_PALETTES[key];
    return `${selectorFor(key, idx === 0)} {\n${paletteToVars(palette)}\n}`;
  });

  const themeBlock = `@theme {
  /* Spacing — base unit 4px matches BRAVEMOVE_SPACE step (p-1=4, p-2=8, …, p-24=96) */
  --spacing: 0.25rem;

  /* Colors */
${(Object.keys(COLOR_TO_VAR) as (keyof ColorPalette)[])
  .map((key) => {
    const tw = COLOR_TO_VAR[key].replace('--bravemove-', '--color-');
    return `  ${tw}: var(${COLOR_TO_VAR[key]});`;
  })
  .join('\n')}

  /* Fonts */
  --font-display: 'Inter', system-ui, sans-serif;
  --font-sans: 'Inter', system-ui, sans-serif;
  --font-mono: 'JetBrains Mono', ui-monospace, monospace;
  --font-arabic: 'Noto Sans Arabic', sans-serif;

  /* Type scale (BRAVEMOVE_SIZE in rem; 1rem = 16px) */
  --text-hero: 4.5rem;
  --text-h1: 2.75rem;
  --text-h2: 2rem;
  --text-h3: 1.375rem;
  --text-body: 0.9375rem;
  --text-small: 0.8125rem;
  --text-caption: 0.6875rem;
  --text-mono: 0.75rem;

  /* Radii (BRAVEMOVE_RADIUS in rem; pill = 999px) */
  --radius-sm: 0.25rem;
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --radius-xl: 1rem;
  --radius-pill: 999px;

  /* Motion */
  --ease-snap: cubic-bezier(0.34, 1.56, 0.64, 1);
  --ease-out: cubic-bezier(0.4, 0, 0.2, 1);
  --ease-in-out: cubic-bezier(0.4, 0, 0.6, 1);
  --duration-tap: 120ms;
  --duration-quick: 200ms;
  --duration-page: 320ms;
  --duration-beat: 1200ms;
  --animate-bravemove-shimmer: bravemove-shimmer calc(var(--duration-beat) * 1.5) linear infinite;
  --animate-bravemove-progress: bravemove-progress var(--duration-beat) var(--ease-in-out) infinite;

  @keyframes bravemove-shimmer {
    from { background-position: 200% 0; }
    to { background-position: -200% 0; }
  }

  @keyframes bravemove-progress {
    from { transform: translateX(-200%); }
    to { transform: translateX(300%); }
  }
}`;

  return `${blocks.join('\n\n')}\n\n${themeBlock}\n`;
}
